/**
 * ESRS S2 — Workers in the Value Chain.
 * Per-supplier working-condition records + entity/year disclosure summary.
 * Backend: backend/routes/s2_worker_routes.py → /api/esrs/s2/{summary,worker-records}.
 * Chrome is EsgKit-only (DESIGN.md tokens); data always mono, status always
 * color + icon + label.
 */
import { useState, useEffect, useCallback } from "react";
import {
  HardHat, Users, Building2, ShieldAlert, Handshake, Plus, Pencil, Trash2,
  CheckCircle2, AlertTriangle, CircleDashed, Clock, Check, X, Save,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt, C, MONO, SANS,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

const LIVING_WAGE = {
  meets_living_wage: { c: C.primary, Icon: CheckCircle2, label: "Meets living wage" },
  below_living_wage: { c: C.destructive, Icon: AlertTriangle, label: "Below living wage" },
  not_assessed: { c: C.neutral, Icon: CircleDashed, label: "Not assessed" },
};

const BLANK = {
  supplier_id: "",
  period_year: 2026,
  workers_permanent: 0,
  workers_temporary: 0,
  workers_migrant: 0,
  safety_incidents: 0,
  collective_bargaining_coverage_pct: "",
  living_wage_status: "not_assessed",
  excessive_hours_flag: false,
  grievance_channel_available: false,
};

export default function S2Workers() {
  const { entities, entityId, setEntityId } = useEntities("esrs_s2_entity_id");
  const [year, setYear] = useState(2026);

  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const [sum, recs] = await Promise.all([
        apiFetch(`/api/esrs/s2/summary?entity_id=${encodeURIComponent(entityId)}&year=${year}`),
        apiFetch(`/api/esrs/s2/worker-records?entity_id=${encodeURIComponent(entityId)}&year=${year}`),
      ]);
      setSummary(sum);
      setRecords(recs?.records || []);
    } catch (e) {
      setError(e.message || "Failed to load S2 worker data");
      setSummary(null);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, year]);

  useEffect(() => { load(); }, [load]);

  // ─── Form ───────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm({ ...BLANK, period_year: year });
    setError("");
    setFormOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.record_id);
    setForm({
      supplier_id: r.supplier_id,
      period_year: r.period_year,
      workers_permanent: r.workers_permanent ?? 0,
      workers_temporary: r.workers_temporary ?? 0,
      workers_migrant: r.workers_migrant ?? 0,
      safety_incidents: r.safety_incidents ?? 0,
      collective_bargaining_coverage_pct:
        r.collective_bargaining_coverage_pct ?? "",
      living_wage_status: r.living_wage_status || "not_assessed",
      excessive_hours_flag: !!r.excessive_hours_flag,
      grievance_channel_available: !!r.grievance_channel_available,
    });
    setError("");
    setFormOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId) return;
    setSaving(true);
    setError("");

    const cb = form.collective_bargaining_coverage_pct;
    const payload = {
      supplier_id: form.supplier_id.trim(),
      period_year: Number(form.period_year),
      workers_permanent: Number(form.workers_permanent) || 0,
      workers_temporary: Number(form.workers_temporary) || 0,
      workers_migrant: Number(form.workers_migrant) || 0,
      safety_incidents: Number(form.safety_incidents) || 0,
      collective_bargaining_coverage_pct:
        cb === "" || cb === null ? null : Number(cb),
      living_wage_status: form.living_wage_status,
      excessive_hours_flag: !!form.excessive_hours_flag,
      grievance_channel_available: !!form.grievance_channel_available,
    };

    try {
      if (editingId) {
        await apiFetch(`/api/esrs/s2/worker-records/${editingId}`, {
          method: "PUT",
          body: payload,
        });
      } else {
        await apiFetch(`/api/esrs/s2/worker-records`, {
          method: "POST",
          body: { entity_id: entityId, ...payload },
        });
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(BLANK);
      await load();
    } catch (e2) {
      // Surfaces the supplier_id + period_year unique-constraint 409 and any
      // 400/422 validation detail from the backend.
      setError(e2.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(
      `Delete the ${r.period_year} worker-condition record for supplier ${r.supplier_id}?`
    )) return;
    setError("");
    try {
      await apiFetch(`/api/esrs/s2/worker-records/${r.record_id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  // ─── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      key: "supplier_id", label: "Supplier",
      render: (r) => <Data>{r.supplier_id}</Data>,
    },
    {
      key: "period_year", label: "Year", align: "right",
      render: (r) => <Data color={C.body}>{r.period_year}</Data>,
    },
    {
      key: "workers_permanent", label: "Perm", align: "right",
      render: (r) => <Data color={C.body}>{fmt(r.workers_permanent, 0)}</Data>,
    },
    {
      key: "workers_temporary", label: "Temp", align: "right",
      render: (r) => <Data color={C.body}>{fmt(r.workers_temporary, 0)}</Data>,
    },
    {
      key: "workers_migrant", label: "Migrant", align: "right",
      render: (r) => <Data color={C.body}>{fmt(r.workers_migrant, 0)}</Data>,
    },
    {
      key: "total_workers", label: "Total", align: "right",
      render: (r) => <Data>{fmt(r.total_workers, 0)}</Data>,
    },
    {
      key: "safety_incidents", label: "Safety inc.", align: "right",
      render: (r) => (
        <Data color={r.safety_incidents > 0 ? C.accent : C.body}>
          {fmt(r.safety_incidents, 0)}
        </Data>
      ),
    },
    {
      key: "collective_bargaining_coverage_pct", label: "CB %", align: "right",
      render: (r) => (
        <Data color={C.body}>
          {r.collective_bargaining_coverage_pct === null ||
          r.collective_bargaining_coverage_pct === undefined
            ? "—"
            : `${fmt(r.collective_bargaining_coverage_pct, 1)}%`}
        </Data>
      ),
    },
    {
      key: "living_wage_status", label: "Living wage",
      render: (r) => {
        const lw = LIVING_WAGE[r.living_wage_status] || LIVING_WAGE.not_assessed;
        return <Badge c={lw.c} Icon={lw.Icon} label={lw.label} />;
      },
    },
    {
      key: "excessive_hours_flag", label: "Hours",
      render: (r) =>
        r.excessive_hours_flag ? (
          <Badge c={C.accent} Icon={Clock} label="Excessive" />
        ) : (
          <Data color={C.muted}>—</Data>
        ),
    },
    {
      key: "grievance_channel_available", label: "Grievance",
      render: (r) =>
        r.grievance_channel_available ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.primary, fontFamily: MONO, fontSize: 11, fontWeight: 600 }}>
            <Check size={13} strokeWidth={1.5} /> Available
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.destructive, fontFamily: MONO, fontSize: 11, fontWeight: 600 }}>
            <X size={13} strokeWidth={1.5} /> None
          </span>
        ),
    },
    {
      key: "_actions", label: "", align: "right",
      render: (r) => (
        <div style={{ display: "inline-flex", gap: 6 }}>
          <Btn variant="ghost" Icon={Pencil} onClick={() => openEdit(r)} title="Edit record">Edit</Btn>
          <Btn variant="ghost" Icon={Trash2} onClick={() => remove(r)} title="Delete record">Delete</Btn>
        </div>
      ),
    },
  ];

  const cbWeighted = summary?.collective_bargaining_coverage_weighted_pct;

  return (
    <Page>
      <PageHeader
        overline="ESRS S2 · Workers in the Value Chain"
        OverlineIcon={HardHat}
        title="Value-Chain Worker Conditions"
        subtitle="Supplier-level working-condition metrics — living wage, safety, collective bargaining, and grievance access across the value-chain workforce."
        entities={entities}
        entityId={entityId}
        onEntityChange={setEntityId}
        onRefresh={load}
        loading={loading}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontFamily: SANS, fontSize: 11, color: C.body }}>Year</span>
            <input
              type="number"
              value={year}
              min={2000}
              max={2100}
              onChange={(e) => setYear(Number(e.target.value) || year)}
              aria-label="Reporting year"
              style={{ ...field, width: 92 }}
            />
          </div>
        }
      />

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {!entityId ? (
        <Card>
          <EmptyState
            Icon={HardHat}
            title="No reporting entity selected"
            body="Select a reporting entity to view its ESRS S2 value-chain worker-condition metrics."
          />
        </Card>
      ) : loading && !summary ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={96} />
          <Skeleton h={240} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* KPI strip */}
          <Card>
            <Overline Icon={Users}>S2 disclosure summary · {year}</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 20,
              }}
            >
              <Metric label="Total workers covered" value={fmt(summary?.total_workers, 0)} />
              <Metric
                label="Suppliers assessed"
                value={fmt(summary?.suppliers_assessed, 0)}
                unit={summary?.suppliers_assessed_pct != null
                  ? `of ${fmt(summary?.suppliers_total, 0)} · ${fmt(summary?.suppliers_assessed_pct, 1)}%`
                  : `of ${fmt(summary?.suppliers_total, 0)}`}
              />
              <Metric
                label="Safety incidents"
                value={fmt(summary?.safety_incidents_total, 0)}
                color={summary?.safety_incidents_total > 0 ? C.accent : C.ink}
              />
              <Metric
                label="Collective bargaining (wtd.)"
                value={cbWeighted != null ? fmt(cbWeighted, 1) : "—"}
                unit={cbWeighted != null ? "%" : undefined}
              />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              <Badge
                c={C.locked}
                Icon={Building2}
                label={`${fmt(summary?.suppliers_with_grievance_channel, 0)} with grievance channel`}
              />
              <Badge
                c={summary?.suppliers_excessive_hours > 0 ? C.accent : C.neutral}
                Icon={Clock}
                label={`${fmt(summary?.suppliers_excessive_hours, 0)} excessive-hours`}
              />
              <Badge
                c={C.primary}
                Icon={Handshake}
                label={`${fmt(summary?.living_wage_status_counts?.meets_living_wage, 0)} meet living wage`}
              />
              <Badge
                c={C.destructive}
                Icon={ShieldAlert}
                label={`${fmt(summary?.living_wage_status_counts?.below_living_wage, 0)} below living wage`}
              />
            </div>
          </Card>

          {/* Records table */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <Overline Icon={HardHat} style={{ marginBottom: 0 }}>Worker-condition records</Overline>
              <Btn Icon={Plus} onClick={openCreate}>New record</Btn>
            </div>

            {records.length === 0 ? (
              <EmptyState
                Icon={HardHat}
                title="No worker-condition records"
                body={`No value-chain worker assessments recorded for ${year}. Add a supplier record to begin tracking living wage, safety, and collective bargaining coverage.`}
              >
                <Btn Icon={Plus} onClick={openCreate}>New record</Btn>
              </EmptyState>
            ) : (
              <DataTable columns={columns} rows={records} keyField="record_id" />
            )}
          </Card>

          {/* Record / edit form */}
          {formOpen && (
            <Card accent={C.primary}>
              <Overline Icon={editingId ? Pencil : Plus}>
                {editingId ? "Edit worker-condition record" : "Record worker conditions"}
              </Overline>
              <form onSubmit={submit}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <Label>Supplier ID *</Label>
                    <input
                      required
                      value={form.supplier_id}
                      disabled={!!editingId}
                      onChange={(e) => setField("supplier_id", e.target.value)}
                      placeholder="supplier_…"
                      style={{ ...field, opacity: editingId ? 0.6 : 1 }}
                    />
                  </div>
                  <div>
                    <Label>Period year *</Label>
                    <input
                      required
                      type="number"
                      min={2000}
                      max={2100}
                      value={form.period_year}
                      disabled={!!editingId}
                      onChange={(e) => setField("period_year", e.target.value)}
                      style={{ ...field, opacity: editingId ? 0.6 : 1 }}
                    />
                  </div>
                  <div>
                    <Label>Workers · permanent</Label>
                    <input type="number" min={0} value={form.workers_permanent}
                      onChange={(e) => setField("workers_permanent", e.target.value)} style={field} />
                  </div>
                  <div>
                    <Label>Workers · temporary</Label>
                    <input type="number" min={0} value={form.workers_temporary}
                      onChange={(e) => setField("workers_temporary", e.target.value)} style={field} />
                  </div>
                  <div>
                    <Label>Workers · migrant</Label>
                    <input type="number" min={0} value={form.workers_migrant}
                      onChange={(e) => setField("workers_migrant", e.target.value)} style={field} />
                  </div>
                  <div>
                    <Label>Safety incidents</Label>
                    <input type="number" min={0} value={form.safety_incidents}
                      onChange={(e) => setField("safety_incidents", e.target.value)} style={field} />
                  </div>
                  <div>
                    <Label>Collective bargaining coverage %</Label>
                    <input type="number" min={0} max={100} step="0.1"
                      value={form.collective_bargaining_coverage_pct}
                      onChange={(e) => setField("collective_bargaining_coverage_pct", e.target.value)}
                      placeholder="0–100" style={field} />
                  </div>
                  <div>
                    <Label>Living-wage status</Label>
                    <select
                      value={form.living_wage_status}
                      onChange={(e) => setField("living_wage_status", e.target.value)}
                      style={field}
                    >
                      {Object.entries(LIVING_WAGE).map(([value, { label }]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 16 }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: SANS, fontSize: 13, color: C.body, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.excessive_hours_flag}
                      onChange={(e) => setField("excessive_hours_flag", e.target.checked)}
                      style={{ accentColor: C.accent, width: 15, height: 15 }}
                    />
                    Excessive working hours flagged
                  </label>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: SANS, fontSize: 13, color: C.body, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.grievance_channel_available}
                      onChange={(e) => setField("grievance_channel_available", e.target.checked)}
                      style={{ accentColor: C.primary, width: 15, height: 15 }}
                    />
                    Grievance channel available
                  </label>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                  <Btn type="submit" Icon={Save} disabled={saving || !form.supplier_id.trim()}>
                    {saving ? "Saving…" : editingId ? "Save changes" : "Create record"}
                  </Btn>
                  <Btn
                    type="button"
                    variant="secondary"
                    onClick={() => { setFormOpen(false); setEditingId(null); setForm(BLANK); }}
                  >
                    Cancel
                  </Btn>
                </div>
              </form>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
